import { mapObject } from '@/util/helpers/object-helpers';
import { randomKey } from '@/util';
import {
  MODAL_INTERACTION,
  MODAL_HIDDEN,
  MODAL_DATA_LOADED,
  VALIDATED_CUSTOM_COLUMNS,
  EVALUATED_CUSTOM_COLUMNS,
  LOADING,
  COLUMNS_ROWS_LOADED,
} from '../actions/constants';
import { COLUMNS } from '../util/enums/EnumLayers';
import CustomColumn from '../util/CustomColumn';
import DataTableCell from '../util/DataTableCell';
import ColumnName from '../util/ColumnName';
import Column from '../util/Column';
import { buildDataTable } from './rows';
import ModalColumnsContainer from '../containers/ModalColumnsContainer';

const INITIAL_STATE = {
  visible: [], // ColumnName objs
  customColumns: [], // CustomColumn objs
  errorMsgs: {}, // string -> string (CustomColumn key mapped to error msgs)
  readyForSave: false,

  customColumnsModified: false, // true if one of the fields of the custom columns has modified (since they were last validated)
  needsValidating: false, // true if customColumns have been modified, and user has finished editing (e.g. no longer in focus)
  validating: false, // true if a request for validation has been sent out to the server (i.e. awaiting result)
  needsEvaluating: false, // true if customColumns are ready to be calculated

  userWantsResults: false, // true if user has clicked to load data rows
  results: null, // DataTable representing results
  dependencies: null, // list of ColumnName objs (the columns that the custom columns depend on)
  customResults: null, // custom column 'key' -> list of DataTableCell objs
};

function interaction(state, payload) {
  switch (payload.section) {
    case 'deleteCustom': {
      const index = state.customColumns.findIndex(
        (cc) => cc === payload.customColumn,
      );
      return {
        ...state,
        customColumns: [
          ...state.customColumns.slice(0, index),
          ...state.customColumns.slice(index + 1),
        ],
        needsValidating: true,
      };
    }
    case 'updateCustom': {
      let customColumnsModified = false;
      if (payload.customColumn) {
        // updated existing
        const index = state.customColumns.findIndex(
          (cc) => cc === payload.customColumn,
        );
        let cc = payload.customColumn;
        if (payload.field === 'statement') {
          cc = cc.setStatement(payload.newValue);
          customColumnsModified = true;
        } else {
          cc = cc.setSelected(!cc.selected());
        }
        return {
          ...state,
          customColumns: [
            ...state.customColumns.slice(0, index),
            cc,
            ...state.customColumns.slice(index + 1),
          ],
          customColumnsModified,
        };
      }
      // new custom column
      const key = randomKey();
      let statement;
      let _customColumnsModified;
      let needsValidating;
      if (payload.field === 'statement') {
        statement = payload.newValue;
        _customColumnsModified = true;
        needsValidating = false;
      } else {
        // checkbox clicked
        statement = 'my_column = "custom column"';
        _customColumnsModified = false;
        needsValidating = true;
      }
      return {
        ...state,
        customColumns: [
          ...state.customColumns,
          new CustomColumn(key, statement, true),
        ],
        customColumnsModified: _customColumnsModified,
        needsValidating,
      };
    }
    case 'optionClicked': {
      // user generated a custom column from another column's dropdown
      const key = randomKey();
      const lhs = `${payload.columnName.name()}_`; // TODO: more descriptive name, e.g. created_at_month
      const rhs = payload.option.replace(
        /column/g,
        payload.columnName.toString(),
      );
      const cc = new CustomColumn(key, `${lhs} = ${rhs}`, true);
      return {
        ...state,
        customColumns: [...state.customColumns, cc],
        needsValidating: true,
      };
    }
    case 'toggleColumn': {
      const selectColumn =
        state.visible.findIndex((sc) => sc.equals(payload.columnName)) === -1;
      if (selectColumn) {
        // add column to selected list
        return { ...state, visible: [...state.visible, payload.columnName] };
      }
      // remove column from selected list
      return {
        ...state,
        visible: state.visible.filter((c) => !c.equals(payload.columnName)),
      };
    }
    case 'finishedEditing': {
      return {
        ...state,
        customColumnsModified: false,
        needsValidating: state.customColumnsModified,
      };
    }
    default:
      throw new Error(`Unknown section:${payload.section}`);
  }
}

function customColumnsInGoodState(state) {
  return (
    !state.customColumnsModified &&
    !state.needsValidating &&
    !state.validating && // can't save if custom cols haven't been validated yet
    Object.keys(state.errorMsgs).length === 0
  ); // can't save if there are errors
}

function readyForSave(state) {
  return (
    (state.visible.length > 0 ||
      state.customColumns.findIndex((cc) => cc.selected()) !== -1) && // must have at least one visible column
    customColumnsInGoodState(state)
  );
}

function needsEvaluating(state) {
  return (
    !!state.results && // user has loaded the cells for other columns
    state.customColumns.length > 0 && // there is at least one custom column to evaluate
    customColumnsInGoodState(state)
  ); //
}

const modalColumns = (state = INITIAL_STATE, action) => {
  switch (action.type) {
    case LOADING: {
      if (action.awaiting === VALIDATED_CUSTOM_COLUMNS) {
        return {
          ...state,
          validating: true,
          needsValidating: false, // reset
          errorMsgs: [], // reset
          dependencies: null, // reset
          customResults: null, // reset
        };
      }
      if (action.awaiting === EVALUATED_CUSTOM_COLUMNS) {
        return { ...state, needsEvaluating: false };
      }
      if (action.awaiting === COLUMNS_ROWS_LOADED) {
        // happens when user has clicked to load rows
        return { ...state, userWantsResults: true };
      }
      return state;
    }
    case COLUMNS_ROWS_LOADED: {
      const newState = {
        ...state,
        results: buildDataTable(action.payload.json),
      };
      newState.needsEvaluating = needsEvaluating(newState);
      return newState;
    }
    case EVALUATED_CUSTOM_COLUMNS: {
      return {
        ...state,
        customResults: mapObject(
          action.payload.json._resultsByKey,
          (key) => key,
          (value) => value.map((c) => DataTableCell.build(c)),
        ),
      };
    }
    case VALIDATED_CUSTOM_COLUMNS: {
      const newState = {
        ...state,
        errorMsgs: action.payload.json._errorMessages,
        dependencies: action.payload.json._dependencies.map((cn) =>
          ColumnName.build(cn),
        ),
        validating: false,
      };
      newState.readyForSave = readyForSave(newState);
      newState.needsEvaluating = needsEvaluating(newState);
      return newState;
    }
    case MODAL_INTERACTION: {
      if (action.payload.modalType !== COLUMNS) {
        return state;
      }
      const newState = interaction(state, action.payload.props);
      newState.readyForSave = readyForSave(newState);
      return newState;
    }
    case MODAL_HIDDEN:
      return INITIAL_STATE; // reset modal
    case MODAL_DATA_LOADED:
      if (action.payload.modal === ModalColumnsContainer) {
        const columns = action.payload.json.map((c) => Column.build(c));
        if (action.payload.existingLayer) {
          // user is editing a 'columns' layer
          const columnNames = action.payload.existingLayer
            .columnNames()
            .filter(
              (cn) => columns.findIndex((c) => c.name().equals(cn)) !== -1,
            );
          const newState = {
            ...state,
            visible: columnNames,
            customColumns: action.payload.existingLayer.customColumns(),
            needsValidating:
              action.payload.existingLayer.customColumns().length > 0,
          };
          newState.readyForSave = readyForSave(newState);
          return newState;
        }
        // new columns layer
        const newState = { ...state, visible: [columns[0].name()] };
        newState.readyForSave = readyForSave(newState);
        return newState;
      }
      return state;
    default:
      return state;
  }
};

export default modalColumns;
